/**
 * 维保记录查询
 */
import React, { useState } from 'react';
import { Card, Form, Row, Col, Button, Tag, message, Select, Input } from 'antd';
import dayjs from 'dayjs';
import { maintenancesRecordRequest, maintenancesRecordRequestPath } from '@/service/carDiagnosis';
import CategoryTreeSelect from '@/components/categoryTreeSelect';
import CommonTable from '@/components/commonTable';
import ExportButton from '@/components/exportButton';
import DatePicker from '@/components/datePicker';
import ContactModal from '@/components/contactModal';
import ParamsModal from '@/components/paramsModal';
import VersionModal from '@/components/versionModal';
import MaintenancePictureModal from '@/components/maintenancePictureModal';
// import Thumbnail from '@/components/thumbnail';
import RenderEllipsisColumn from "@/components/renderEllipsisColumn";
import { operateDayRange } from '@/utils/operateSearchParams';
import { useUserCateIdArr } from '@/utils/ownHooks';
import { useTable } from '@/utils/useTable';
import { snColumnConfig, cameraSnColumnConfig, cateGroupColumnConfig, frameNumberColumnConfig } from '@/constants/tableColumn';
import { workOrderTypeData } from '@/constants/workOrder';
import styles from '@/styles/pageContent.module.scss';

const { RangePicker } = DatePicker;

const maintenanceMethodMap: Record<number, string> = {
  0: '默认未选择',
  1: '远程处理',
  2: '线下处理',
};

const MaintenanceLogSearch = () => {
  const cateIdArr = useUserCateIdArr();
  const [searchParams, setSearchParams] = useState({
    beginDate: `${dayjs().subtract(7, 'day').format('YYYY-MM-DD')}`,
    endDate: `${dayjs().subtract(1, 'day').format('YYYY-MM-DD')}`,
    cateId: cateIdArr,
  }); // 搜索相关的参数
  const [operateInfo, setOperateInfo] = useState<any>({}); // 表格操作的信息
  const [form] = Form.useForm();
  const { page, limit, loading, data, totalCount, onPageChange } = useTable({
    requestFunc: maintenancesRecordRequest,
    initPageSize: 40,
    searchParams,
  });

  // 搜索
  const onSearch = async (values: any) => {
    if (values.cateId == '') {
      message.warning('分类选择不能为空')
    } else {
      const newSearchParams = operateDayRange(values);
      setSearchParams(newSearchParams);
    }
  };

  // 获取导出的参数
  const getExportParams = () => {
    const values = form.getFieldsValue();
    if (values.cateId == '') {
      message.warning('分类选择不能为空')
    } else {
      return operateDayRange(values, true);
    }
  }

  // 表格列显示
  const columns = [
    {
      title: '时间',
      dataIndex: 'time',
      width: 130,
      ellipsis: { showTitle: false },
      render: (value: string) => <RenderEllipsisColumn text={value} />,
    },
    snColumnConfig,
    {
      title: '车牌号',
      dataIndex: 'plate_number',
      width: 110,
    },
    cameraSnColumnConfig,
    cateGroupColumnConfig,
    frameNumberColumnConfig,
    {
      title: '维保负责人',
      dataIndex: 'maintainer',
      width: 120,
      ellipsis: { showTitle: false },
      render: (value: string) => <RenderEllipsisColumn text={value} />,
    },
    {
      title: '客户联系人',
      dataIndex: 'contactName',
      width: 120,
      ellipsis: { showTitle: false },
      render: (value: string) => <RenderEllipsisColumn text={value} />,
    },
    {
      title: '联系人电话',
      dataIndex: 'contactPhone',
      width: 120,
      ellipsis: { showTitle: false },
      render: (value: string) => <RenderEllipsisColumn text={value} />,
    },
    {
      title: '维护地点',
      dataIndex: 'address',
      width: 120,
      ellipsis: { showTitle: false },
      render: (value: string) => <RenderEllipsisColumn text={value} />,
    },
    {
      title: '处理故障项',
      dataIndex: 'malfunctions',
      width: 120,
      ellipsis: { showTitle: false },
      render: (value: Array<string>) => <RenderEllipsisColumn text={(value || []).join('，')} />,
    },
    {
      title: '维护明细',
      dataIndex: 'maintenances',
      width: 120,
      ellipsis: { showTitle: false },
      render: (value: Array<string>) => <RenderEllipsisColumn text={(value || []).join('，')} />,
    },
    // {
    //   title: '维保图片/视频',
    //   dataIndex: 'resources',
    //   width: 170,
    //   ellipsis: { showTitle: false },
    //   render: (value: any) => (<Thumbnail
    //     dataSource={value || []}
    //     downloadImgName="维保图片"
    //     downloadVideoName="维保视频"
    //   />),
    // },
    {
      title: '维保标签',
      dataIndex: 'tags',
      width: 140,
      ellipsis: { showTitle: false },
      render: (value: any) => <div className={styles.tableTagColumn}>
        {(Object.values(value || {})[0] as any || []).map(((item1: any) => <Tag key={item1.tagId}>{item1.title}</Tag>))}
      </div>,
    },
    {
      title: 'aeb设备类型',
      dataIndex: 'aebType',
      width: 140,
    },
    {
      title: 'adas 设备类型',
      dataIndex: 'adasType',
      width: 140,
    },
    {
      title: '运维设备类型',
      dataIndex: 'currentDeviceTypeStr',
      width: 140,
    },
    {
      title: '运维方式',
      dataIndex: 'type',
      width: 140,
      render: (value: number) => {
        return maintenanceMethodMap[Number(value)] || maintenanceMethodMap[0];
      },
    },
    {
      title: '钉钉状态',
      dataIndex: 'dingState',
      width: 140,
    },
    {
      title: '操作',
      dataIndex: 'operate',
      width: 160,
      fixed: 'right' as 'right',
      render: (value: any, item: any) => {
        return (
          <div className={styles.tableOperateColumn}>
            <Button type="link" onClick={() => setOperateInfo({ type: 'maintenancePicture', data: item })}>维保照片</Button>
            <Button type="link" onClick={() => setOperateInfo({ type: 'contact', data: item })}>联系方式</Button>
            <Button type="link" onClick={() => setOperateInfo({ type: 'params', data: item })}>参数信息</Button>
            <Button type="link" onClick={() => setOperateInfo({ type: 'version', data: item })}>版本信息</Button>
          </div>
        )
      },
    },
  ]

  return (
    <Card bordered={false} className={styles.fixPageContentBox}>
      <Form onFinish={onSearch} form={form}>
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item
              label="日期范围"
              name="time"
              initialValue={[dayjs(searchParams.beginDate), dayjs(searchParams.endDate)]}
            >
              <RangePicker allowClear={false} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="分类选择" name="cateId" initialValue={searchParams.cateId}>
              <CategoryTreeSelect treeCheckable />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="工单类型" name="type">
              <Select
                options={workOrderTypeData}
                allowClear
                showArrow
                placeholder="请选择工单类型"
                optionFilterProp="label"
                maxTagCount="responsive"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
        <Col span={6}>
            <Form.Item label="维保人员" name="name">
              <Input placeholder="请输入维保人员" allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item>
              <div className={styles.searchBox}>
                <Button type="primary" htmlType="submit">搜索</Button>
                <ExportButton 
                  url={maintenancesRecordRequestPath}
                  fileName='维保记录查询'
                  getParamsFunc={getExportParams}
                  requestMethod="GET"
                  permissionKey='/car-admin/maintenance-log/export'
                />
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <CommonTable
        rowKey="mainId"
        columns={columns}
        dataSource={data}
        pagination={{
          total: totalCount,
          current: page,
          pageSize: limit,
          onChange: onPageChange,
        }}
        bordered
        loading={loading}
      />
      {operateInfo.type === 'contact' &&
        <ContactModal data={operateInfo.data} closeModal={() => setOperateInfo({})} />
      }
      {operateInfo.type === 'params' &&
        <ParamsModal data={operateInfo.data} closeModal={() => setOperateInfo({})} />
      }
      {operateInfo.type === 'version' &&
        <VersionModal data={operateInfo.data} closeModal={() => setOperateInfo({})} />
      }
      {operateInfo.type === 'maintenancePicture' &&
        <MaintenancePictureModal data={operateInfo.data} closeModal={() => setOperateInfo({})} />
      }
    </Card>
  )
}

export default MaintenanceLogSearch;
  